/* global google */
import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../../utils/loadGoogleMaps';

// Admin Community Analytics: where people are, as a heat map — each person a
// soft, see-through circle, so overlaps build up where they cluster. Colour
// by group: members (red), waiting list (amber), searchers (blue). Points are
// rounded to ~1km on the server.
const COLOURS = { member: '#ef4444', waitlist: '#f59e0b', search: '#3b82f6' };
const LABELS = { member: 'Members', waitlist: 'Waiting list', search: 'Searchers' };
const KIND = { member: 'Member', waitlist: 'Waiting list', search: 'Searcher' };

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const fmtVal = (v) => (Array.isArray(v) ? v.join(', ') : String(v));

// Hovering a point: who it is (the server's mapPoints[].info) — members'
// name, photo, bio and suggestion numbers; waiting list / searchers' answers.
const cardHtml = (p) => {
  const i = p.info || {};
  const colour = COLOURS[p.kind] || COLOURS.member;
  const head = p.kind === 'member'
    ? `<div class="as-hm-head">${i.pic ? `<img src="${esc(i.pic)}" alt="" />` : '<div class="as-hm-nopic"></div>'}
        <div><b>${esc(i.name)}</b>${i.age ? `, ${esc(i.age)}` : ''}
        <div class="as-hm-sub">${[i.answers?.Gender, i.localArea].filter(Boolean).map(esc).join(' · ')}</div></div></div>`
    : `<div><b>${esc(i.email || 'Anonymous search')}</b>${i.age ? `, ${esc(i.age)}` : ''}
        <div class="as-hm-sub">${esc(i.localArea || '')}</div></div>`;
  const when = `${p.kind === 'member' ? 'Joined' : p.kind === 'waitlist' ? 'Left email' : 'Searched'} ${esc(fmtDay(i.at))}${i.community ? ` · ${esc(i.community)}` : ''}`;
  const m = i.metrics;
  const metrics = m
    ? `<div class="as-hm-metrics"><span><b>${m.suggested}</b> suggested</span><span><b>${m.interested}</b> interested</span><span><b>${m.notInterested}</b> not interested</span></div>`
    : (i.search && i.search.matchCount != null
      ? `<div class="as-hm-metrics"><span>Found <b>${esc(i.search.matchCount)}</b>${i.search.radiusMiles ? ` within ${esc(i.search.radiusMiles)} mi` : ''}</span></div>` : '');
  const answers = Object.entries(i.answers || {}).filter(([k]) => !(p.kind === 'member' && k === 'Gender') && k !== 'Age')
    .map(([k, v]) => `<div><span>${esc(k)}:</span> ${esc(fmtVal(v))}</div>`).join('');
  return `<div class="as-hm-card">
    <div class="as-hm-kind" style="color:${colour}">● ${KIND[p.kind] || ''}</div>
    ${head}
    ${i.bio ? `<div class="as-hm-bio">${esc(i.bio)}</div>` : ''}
    ${metrics}
    ${answers ? `<div class="as-hm-answers">${answers}</div>` : ''}
    <div class="as-hm-when">${when}</div>
  </div>`;
};

const CommunityHeatMap = ({ points }) => {
  const boxRef = useRef(null);
  const mapRef = useRef(null);
  const circlesRef = useRef([]);
  const infoRef = useRef(null);
  const pinnedRef = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(() => {
      if (cancelled || !boxRef.current) return;
      if (!mapRef.current) {
        mapRef.current = new google.maps.Map(boxRef.current, {
          center: { lat: 54.5, lng: -3 },
          zoom: 5,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
        });
      }
      const map = mapRef.current;
      if (!infoRef.current) {
        infoRef.current = new google.maps.InfoWindow({ disableAutoPan: true });
        infoRef.current.addListener('closeclick', () => { pinnedRef.current = false; });
      }
      const info = infoRef.current;
      info.close();
      pinnedRef.current = false;
      circlesRef.current.forEach(c => c.setMap(null));
      circlesRef.current = [];
      if (!points?.length) return;
      const bounds = new google.maps.LatLngBounds();
      points.forEach(p => {
        const colour = COLOURS[p.kind] || COLOURS.member;
        // A soft halo and a smaller, stronger core, the same size on screen
        // at any zoom — overlaps read as heat.
        [[22, 0.14], [9, 0.45]].forEach(([scale, opacity]) => {
          const core = scale === 9;
          const marker = new google.maps.Marker({
            map,
            position: { lat: p.lat, lng: p.lng },
            // Only the core takes the mouse: hover shows the card, click
            // keeps it open until it's closed or another is clicked.
            clickable: core,
            zIndex: core ? 2 : 1,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale,
              fillColor: colour,
              fillOpacity: opacity,
              strokeWeight: 0,
            },
          });
          if (core) {
            // Hover leaves the map still; a click pans so the whole card shows.
            const open = () => { info.setOptions({ disableAutoPan: !pinnedRef.current }); info.setContent(cardHtml(p)); info.open({ map, anchor: marker }); };
            marker.addListener('mouseover', () => { if (!pinnedRef.current) open(); });
            marker.addListener('mouseout', () => { if (!pinnedRef.current) info.close(); });
            marker.addListener('click', () => { pinnedRef.current = true; open(); });
          }
          circlesRef.current.push(marker);
        });
        bounds.extend({ lat: p.lat, lng: p.lng });
      });
      if (points.length === 1) { map.setCenter(bounds.getCenter()); map.setZoom(9); } else map.fitBounds(bounds, 40);
    }).catch(() => { if (!cancelled) setError("Couldn't load the map."); });
    return () => { cancelled = true; };
  }, [points]);

  const kinds = [...new Set((points || []).map(p => p.kind))];
  return (
    <div>
      {error && <div className="as-error">{error}</div>}
      <div ref={boxRef} className="as-cs-map" />
      <div className="as-cs-map-legend">
        {kinds.map(k => (
          <span key={k}><i style={{ background: COLOURS[k] }} /> {LABELS[k]} ({points.filter(p => p.kind === k).length})</span>
        ))}
        {!points?.length && <span>No one with a location in this period.</span>}
      </div>
    </div>
  );
};

export default CommunityHeatMap;
